import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from '@tanstack/react-router';
import { openLink } from '@telegram-apps/sdk-react';
import axios from 'axios';
import moment from 'moment';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { z } from 'zod';

import { logger } from '@app/logger';

import { useTelegramBackButton } from '@common/hooks';
import { useTabbar } from '@common/providers/useTabbar';
import { appInstanceService } from '@common/services/appInstanceService';
import { signInTma, AuthFailureReason } from '@common/services/tokenService';
import { BottomSheet } from '@common/ui/BottomSheet';
import { Button } from '@common/ui/buttons/Button';
import { Steps } from '@common/ui/Steps';
import { Paragraph } from '@common/ui/typography/Paragraph';
import { useEventEmit } from '@common/utils/events';
import { OFERTA_URL, PRIVACY_POLICY_URL } from '@common/utils/legalLinks';
import { useRequestSubmit } from '@common/utils/requestSubmitPolyfill';
import { safeTma } from '@common/utils/safeTma';

import { useAuthStore } from '@entities/auth/stores/useAuthStore';
import { isStorableUser } from '@entities/auth/utils';

import { analytics } from '@services/AnalyticsService';
import { type PostRegisterTmaParams } from '@services/AuthService';
import { usePostAuth, usePostRegisterTma } from '@services/hooks/AuthService';

import { About, BasicInfo, Pictures } from './components';

const basicInfoSchema = z.object({
  name: z
    .string({ message: 'Введите имя' })
    .trim()
    .min(2, 'Не менее 2-х символов')
    .regex(/^[^\d]*$/, 'Не используйте цифры')
    .regex(/^[а-яёА-ЯЁ ]*$/, 'Только русские буквы')
    .regex(/^(?!.*[^аеёиоуыэюя]{4}).*$/i, 'Не более трех согласных подряд'),
  dateOfBirth: z
    .any({ message: 'Введите дату' })
    .refine((date) => {
      return date?.isValid();
    }, 'Введите корректную дату')
    .refine((date) => {
      const age = moment().diff(date, 'years');

      return age >= 18;
    }, 'Простите, но мы не пускаем людей младше 18 лет')
    .refine((date) => {
      const age = moment().diff(date, 'years');

      if (age < 0) {
        return false;
      }

      return age <= 80;
    }, 'Боимся, что вашему возрасту не подойдет наша аудитория'),
  gender: z.enum(['male', 'female']),
  goal: z.string({ message: 'Выберите цель' }).min(1, 'Выберите вариант'),
  city: z.string({ message: 'Выберите город' }).min(1, 'Выберите город'),
});

const aboutSchema = z.object({
  about: z
    .string()
    .trim()
    .min(10, 'Не менее 10 символов')
    .refine(
      (val) => !/(https?:\/\/[^\s]+)/g.test(val),
      'Нельзя использовать ссылки',
    ),
});

const picturesSchema = z.object({
  pictures: z.array(z.object({ uploadURL: z.string() }), {
    required_error: 'Добавьте хотя бы одну фотографию',
  }),
});

type BasicInfoSchema = z.infer<typeof basicInfoSchema>;
type AboutSchema = z.infer<typeof aboutSchema>;
type PicturesSchema = z.infer<typeof picturesSchema>;

type FormData = BasicInfoSchema & AboutSchema & PicturesSchema;

export type SignUpFormData = FormData;

const BOUND_APP_INSTANCE_ERROR_MESSAGE =
  'app_instance_id уже привязан к пользователю';

type RegisterTma = (payload: PostRegisterTmaParams) => Promise<unknown>;

const getProperty = (source: object, property: string): unknown =>
  Object.getOwnPropertyDescriptor(source, property)?.value;

const getResponseErrorMessages = (data: unknown): string[] => {
  if (!data || typeof data !== 'object') {
    return [];
  }

  const errors = getProperty(data, 'errors');
  const message = getProperty(data, 'message');
  const messages: string[] = [];

  if (errors && typeof errors === 'object') {
    const nestedMessage = getProperty(errors, 'message');

    if (typeof nestedMessage === 'string') {
      messages.push(nestedMessage);
    }
  }

  if (typeof message === 'string') {
    messages.push(message);
  }

  return messages;
};

const isBoundAppInstanceRegistrationError = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    return getResponseErrorMessages(error.response?.data).includes(
      BOUND_APP_INSTANCE_ERROR_MESSAGE,
    );
  }

  return (
    error instanceof Error && error.message === BOUND_APP_INSTANCE_ERROR_MESSAGE
  );
};

const buildRegisterTmaPayload = (
  data: FormData,
  appInstanceId: string,
): PostRegisterTmaParams => {
  const isMale = data.gender === 'male';

  return {
    app_instance_id: appInstanceId,
    username: data.name,
    // The picked calendar day as a plain date. toISOString() would send its
    // local midnight in UTC, which is the day before east of UTC (Moscow).
    dob: data.dateOfBirth.format('YYYY-MM-DD'),
    about: data.about,
    goal: data.goal,
    city: data.city,
    looking_for: isMale ? 1 : 0,
    sex: isMale ? 0 : 1,
    photos: data.pictures.map((picture) => picture.uploadURL || ''),
  };
};

const registerTmaWithFreshAppInstanceRetry = async ({
  data,
  register,
  getAppInstanceId,
  refreshAppInstanceId,
}: {
  data: FormData;
  register: RegisterTma;
  getAppInstanceId: () => Promise<string>;
  refreshAppInstanceId: () => Promise<string>;
}) => {
  const appInstanceId = await getAppInstanceId();

  try {
    await register(buildRegisterTmaPayload(data, appInstanceId));
  } catch (error) {
    if (!isBoundAppInstanceRegistrationError(error)) {
      throw error;
    }

    const freshAppInstanceId = await refreshAppInstanceId();
    await register(buildRegisterTmaPayload(data, freshAppInstanceId));
  }
};

export const SignUp = () => {
  const navigate = useNavigate();
  const tabbar = useTabbar();
  const emit = useEventEmit();
  const { trigger: triggerPostAuth } = usePostAuth();
  const { setUser, setSwipeStatus, setVerificationStatus, setReferrals } =
    useAuthStore();

  const formRef = useRef<HTMLFormElement>(null);
  const isSubmittingSignUpRef = useRef(false);
  const [formData, setFormData] = useState<FormData>();
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [isSubmittingSignUp, setIsSubmittingSignUp] = useState(false);
  const [, setErudaTapCount] = useState<number>(0);

  const handleBackClick = useCallback(() => {
    setCurrentStep((prev) => prev - 1);
  }, []);

  useTelegramBackButton({
    enabled: currentStep > 0,
    onClick: handleBackClick,
  });

  const {
    trigger: triggerPostRegisterTma,
    isMutating: isMutatingPostRegisterTma,
  } = usePostRegisterTma();

  useEffect(() => {
    analytics.trackSignUpStarted('initial');
  }, []);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData, { currentStep: number }>({
    mode: 'onBlur',
    context: {
      currentStep,
    },
    defaultValues: {
      gender: 'male',
    },
    resolver: (async (data, context, options) => {
      const schemas: Record<number, z.ZodTypeAny> = {
        0: basicInfoSchema,
        1: aboutSchema,
        2: picturesSchema,
      };

      const schema = schemas[(context as { currentStep: number }).currentStep];
      const innerResolver = zodResolver<FormData>(
        schema as unknown as z.ZodType<FormData>,
      );
      return await innerResolver(
        data,
        context as { currentStep: number },
        options,
      );
    }) as Resolver<FormData, { currentStep: number }>,
  });

  const submitSignUp = async (data: FormData) => {
    if (isSubmittingSignUpRef.current) return;

    isSubmittingSignUpRef.current = true;
    setIsSubmittingSignUp(true);

    try {
      await registerTmaWithFreshAppInstanceRetry({
        data,
        register: triggerPostRegisterTma,
        getAppInstanceId: appInstanceService.getOrCreateTmaAppInstanceId,
        refreshAppInstanceId: appInstanceService.refreshTmaAppInstanceId,
      });

      try {
        const signInResult = await signInTma();

        if (!signInResult.success) {
          throw signInResult.reason === AuthFailureReason.NETWORK_ERROR
            ? signInResult.error
            : new Error('Registered user sign-in failed');
        }

        const authData = await triggerPostAuth();

        // `trigger()` can resolve `undefined` (see isStorableUser), so the
        // response is checked before anything is read from it. The catch
        // below reports it and shows the profile error.
        if (!isStorableUser(authData?.user)) {
          throw new Error('POST /auth answered without a user (SignUp)');
        }

        setUser(authData.user);
        setReferrals(authData.referrals);
        setSwipeStatus(authData.swipe_status);
        setVerificationStatus(authData.verification_status);
      } catch (authError) {
        logger.captureException(authError, {
          tags: { section: 'SignUp post-registration auth' },
        });

        emit('error', {
          title: 'Не удалось загрузить профиль',
          description: 'Попробуйте перезагрузить приложение',
        });
        return;
      }

      analytics.trackSignUpCompleted('direct');
      navigate({ to: '/dating' });
    } catch (e) {
      logger.captureException(e, {
        tags: {
          section: 'Ошибка /Данные не загрузились/ при регистрации',
        },
      });
      emit('error', {
        title: 'Данные не загрузились',
        description: 'Попробуйте ещё раз',
      });
    } finally {
      isSubmittingSignUpRef.current = false;
      setIsSubmittingSignUp(false);
    }
  };

  const onSubmit = async (data: FormData) => {
    const nextFormData = { ...formData, ...data };

    setFormData(nextFormData);

    if (currentStep < 2) {
      setCurrentStep((prev) => prev + 1);
      return;
    }

    await submitSignUp(nextFormData);
  };

  const requestSubmit = useRequestSubmit(formRef);
  useEffect(() => {
    tabbar.setBottomSheet(
      <BottomSheet
        buttons={[
          <Button
            testId="sign-up-next-button"
            loading={isMutatingPostRegisterTma || isSubmittingSignUp}
            disabled={isMutatingPostRegisterTma || isSubmittingSignUp}
            onClick={() => requestSubmit()}
          >
            Далее
          </Button>,
        ]}
      />,
    );
  }, [
    currentStep,
    isMutatingPostRegisterTma,
    isSubmittingSignUp,
    requestSubmit,
    tabbar,
  ]);

  return (
    <form
      ref={formRef}
      onSubmit={handleSubmit(onSubmit)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'start',
        gridGap: '12px',
      }}
    >
      <Steps
        testId="sign-up-steps"
        steps={3}
        currentStep={currentStep + 1}
        onStepClick={(step) => {
          if (step !== 3) return;
          setErudaTapCount((prev) => {
            const next = prev + 1;
            if (next === 10) {
              //@ts-expect-error - eruda is a global debug library
              eruda.init();
              return 0;
            }
            return next;
          });
        }}
      />
      {currentStep === 0 && <BasicInfo control={control} errors={errors} />}
      {currentStep === 1 && <About control={control} errors={errors} />}
      {currentStep === 2 && <Pictures control={control} errors={errors} />}
      {/* Informational only — registration is not gated on these links. The
          documents are served by the backend outside the SPA, so open them
          externally. */}
      <Paragraph
        variant="secondary"
        align="center"
        style={{ fontSize: 12, lineHeight: '16px' }}
      >
        Регистрируясь, вы принимаете{' '}
        <a
          href={OFERTA_URL}
          // target=_blank is load-bearing, not decoration. When openLink
          // throws we deliberately let the browser follow the href — but an
          // in-place navigation inside the Telegram WebView would replace the
          // mini app with the legal page and destroy a half-filled
          // registration form, with no chrome to get back. Opening a new
          // context keeps the form alive.
          target="_blank"
          rel="noopener noreferrer"
          data-test-id="sign-up-oferta-link"
          style={{ color: 'inherit', textDecoration: 'underline' }}
          onClick={(event) => {
            // Only suppress native navigation if Telegram actually opened the
            // link; otherwise the href above is the fallback.
            if (safeTma('signUp.openLink.oferta', () => openLink(OFERTA_URL))) {
              event.preventDefault();
            }
          }}
        >
          условия использования
        </a>{' '}
        и{' '}
        <a
          href={PRIVACY_POLICY_URL}
          // See the oferta link above — target=_blank keeps the in-progress
          // registration form alive when the href fallback is used.
          target="_blank"
          rel="noopener noreferrer"
          data-test-id="sign-up-privacy-link"
          style={{ color: 'inherit', textDecoration: 'underline' }}
          onClick={(event) => {
            // See the oferta link above: native href is the fallback.
            if (
              safeTma('signUp.openLink.privacy', () =>
                openLink(PRIVACY_POLICY_URL),
              )
            ) {
              event.preventDefault();
            }
          }}
        >
          политику конфиденциальности
        </a>
      </Paragraph>
    </form>
  );
};
